/**
 * SASADM-0154 — one contrast failure row (two swatches, "Light · text on bg ·
 * 3.3 : 1 (needs 4.5)", an optional blocking caption). Clicking toggles the
 * highlight on both swatches. Shared by the Theme tab and, since SASADM-0159,
 * the bundle import report, so the two render failures identically.
 */
import { Box, Typography } from '@mui/material';
import type { ContrastFailure } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

function Swatch({
  color,
  highlighted,
  label,
}: {
  color: string;
  highlighted: boolean;
  label: string;
}) {
  return (
    <Box
      role="img"
      aria-label={label}
      data-highlighted={highlighted ? 'true' : undefined}
      sx={{
        width: 20,
        height: 20,
        borderRadius: 0.5,
        border: 1,
        borderColor: 'divider',
        flexShrink: 0,
        outline: highlighted ? 2 : 0,
        outlineColor: 'primary.main',
        outlineStyle: 'solid',
        outlineOffset: 1,
      }}
      style={{ backgroundColor: color }}
    />
  );
}

interface ContrastFailureRowProps {
  failure: ContrastFailure;
  highlighted: boolean;
  onToggle: () => void;
  /** The Theme tab's caption; omitted where blocking isn't per row (the bundle report). */
  status?: 'blocking' | 'existing';
}

export function ContrastFailureRow({
  failure: f,
  highlighted,
  onToggle,
  status,
}: ContrastFailureRowProps) {
  const { t } = useCopy();
  const mode =
    f.mode === 'dark'
      ? t('brands.theme.preview.dark', 'Dark')
      : t('brands.theme.preview.light', 'Light');
  const label = t(
    'brands.theme.contrast.row',
    '{{mode}} · {{fg}} on {{bg}} · {{ratio}} : 1 (needs {{required}})',
    {
      mode,
      fg: f.fg,
      bg: f.bg,
      ratio: f.ratio.toFixed(1),
      required: String(f.required),
    },
  );
  return (
    <Box
      component="li"
      role="button"
      tabIndex={0}
      aria-pressed={highlighted}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        cursor: 'pointer',
        listStyle: 'none',
      }}
    >
      <Swatch color={f.fgColor} highlighted={highlighted} label={`${f.fg} ${f.fgColor}`} />
      <Swatch color={f.bgColor} highlighted={highlighted} label={`${f.bg} ${f.bgColor}`} />
      <Typography variant="body2">{label}</Typography>
      {status ? (
        <Typography variant="caption" color={status === 'blocking' ? 'error' : 'text.secondary'}>
          {status === 'blocking'
            ? t('brands.theme.contrast.blocking', 'Blocks save')
            : t('brands.theme.contrast.existing', 'Existing — does not block')}
        </Typography>
      ) : null}
    </Box>
  );
}
